import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column, SortingRule } from 'react-table';
import SimpleReactTable from '../../../../../../generic/SimpleReactTable';
import TableHeader from '../../../../../../generic/TableHeader';
import TeamGroupMembersTableRoleDropdown from './TeamGroupMembersTableRoleDropdown';
import AddTeamGroupMemberModal from './AddTeamGroupMemberModal';
import TeamGroupMembersTableRemove from './TeamGroupMembersTableRemove';
import SettingsPageTableActions from '../../../../../../shared/settings/SettingsPageTableActions';
import type { TeamGroupUser } from '../../../../../../../../shared/types/routes/teamGroups';

const defaultSortMode: [SortingRule] = [
  {
    id: 'name',
    desc: false,
  },
];

const useTeamGroupMembersColumns = (
  sortMode: SortingRule,
  teamGroupId: number,
): Array<Column<TeamGroupUser>> => useMemo(() => [
  {
    id: 'username',
    Header: <TableHeader title="Name" sort={{ id: 'username', currentMode: sortMode }} />,
    accessor: 'username',
    resizable: false,
    width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'email',
    Header: <TableHeader title="Email" sort={{ id: 'email', currentMode: sortMode }} />,
    accessor: 'email',
    resizable: false,
    width: 350,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'role',
    Header: <TableHeader title="Role" sort={{ id: 'role', currentMode: sortMode }} />,
    accessor: 'roleId',
    resizable: false,
    Cell: ({ original }) => (
      <TeamGroupMembersTableRoleDropdown
        roleId={original?.roleId}
        userId={original?.userId}
        teamGroupId={teamGroupId}
      />
    ),
    style: { overflow: 'visible' },
  },
  {
    id: 'remove',
    Header: <TableHeader title="Remove" sort={{ id: 'remove', currentMode: sortMode }} />,
    resizable: false,
    width: 100,
    headerStyle: {
      display: 'flex',
      justifyContent: 'center',
    },
    style: {
      display: 'flex',
      justifyContent: 'center',
    },
    Cell: ({ original }) => (
      <TeamGroupMembersTableRemove
        userId={original?.userId}
        teamGroupId={teamGroupId}
      />
    ),
  },
], [sortMode, teamGroupId]);

type Props = {
  isLoading: boolean;
  teamGroupId: number;
  teamGroupName: string;
  members: TeamGroupUser[];
};

/**
 * Displays the members of a team group, along with
 * the ability to search, sort and add new members.
 */
const TeamGroupMembersTable: VoidFunctionComponent<Props> = ({ members, isLoading, teamGroupId, teamGroupName }) => {
  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode[0]);
  const columns = useTeamGroupMembersColumns(sortMode, teamGroupId);
  const [search, setSearch] = useState('');
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);

  const searchLowerCase = search.toLowerCase();
  const filteredMembers = useMemo(() =>
    members.filter((member) => member.username?.toLowerCase().includes(searchLowerCase)), [members, searchLowerCase]);

  //  This helps us determine which members have already been added to the team group.
  const previouslyAddedMembers = useMemo(() => new Set(members.map(member => member.userId)), [members]);

  return (
    <>
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="add-member-to-team-group-button"
          size="large"
          type="secondary"
          onClick={() => setIsAddMemberModalOpen(true)}
          isDisabled={false}
          showTooltip={false}
          tooltip="You are not permitted to add members to this team group."
        >
          Add Member
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.SearchInput
          label="Search team group members"
          value={search}
          onChange={setSearch}
          placeholder="Search team group members"
          size="medium"
        />
      </SettingsPageTableActions>
      <SimpleReactTable
        data={filteredMembers}
        columns={columns}
        defaultSortMode={defaultSortMode}
        onSortedChanged={setSortMode}
        showPagination={filteredMembers.length > 20}
        cacheId="organizationTeamGroupMembers"
        isLoading={isLoading}
      />
      <AddTeamGroupMemberModal
        isOpen={isAddMemberModalOpen}
        onHide={() => setIsAddMemberModalOpen(false)}
        teamGroupId={teamGroupId}
        teamGroupName={teamGroupName}
        previouslyAddedMembers={previouslyAddedMembers}
      />
    </>
  );
};

export default TeamGroupMembersTable;
